import { useEffect, useState } from 'react';
import {
  DTRepairer_Public,
  FBD_Repairer_Private,
  FBD_Repairer_Public,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import { useGetMySagePersonas } from '@rabbit/data/portal';

import { useTranslation } from 'react-i18next';
import { checkIfAllowedEmailForUser } from '../../shared/helpers';

/**
 * A custom hook that retrieves and provides a list of installer peers and their loading state.
 *
 * This hook fetches installer peer data by calling APIs, processes the responses,
 * filters the peers based on specific conditions, and updates the state to reflect
 * the peer data and the loading status.
 *
 * @return {Object} An object containing the following properties:
 * - installerPeer {DTRepairer_Public[]} The list of installer peers after filtering.
 * - installerPeerLoading {boolean} The loading state indicating if the peer data is still being fetched.
 */
export function useGetInstallerPeers() {
  const [installerPeerLoading, setInstallerPeerLoading] = useState(true);
  const [installerPeer, setInstallerPeer] = useState<DTRepairer_Public[]>([]);
  const { installerPersona, repairerPersona } = useGetMySagePersonas();
  const { t } = useTranslation();
  useEffect(() => {
    const filterPeers = async (): Promise<DTRepairer_Public[]> => {
      try {
        const fetchInstaller = await FBD_Repairer_Private.get(
          `${PersonaTypeSingleLetter.Installer}:${t('tenantLink')}`
        );
        const idArr = Object.keys(fetchInstaller?.delegates ?? {}).filter(
          (id) =>
            id !== `${PersonaTypeSingleLetter.Installer}:${t('tenantLink')}`
        );
        const promises = idArr.map((id) => FBD_Repairer_Public.get(id));
        const peerData = await Promise.all(promises);
        const environment: string = import.meta.env.VITE_FIREBASE_MODE;

        return peerData.filter(
          (item): item is DTRepairer_Public =>
            !!item && !checkIfAllowedEmailForUser(item.email!, environment)
        );
      } catch (error) {
        console.error('Error filtering peers:', error);
        return [];
      }
    };

    const fetchPeers = async (): Promise<void> => {
      try {
        const filteredPeers = await filterPeers();
        setInstallerPeer(filteredPeers);
      } catch (error) {
        console.error('Error fetching peers:', error);
      } finally {
        setInstallerPeerLoading(false);
      }
    };

    fetchPeers();
  }, [
    repairerPersona?.repairer_private?.principals?.[0],
    installerPersona?.personaId,
  ]);

  return { installerPeer, installerPeerLoading };
}
